import * as React from 'react';
import type { LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import {
  Cartao,
  CartaoCabecalho,
  CartaoConteudo,
  CartaoDescricao,
  CartaoTitulo,
} from '@/components/ui/card';

interface IndicadorProps {
  rotulo: string;
  /** Já formatado: aqui aparecem horas, dias e percentuais, não só inteiros. */
  valor: string;
  descricao?: string;
  icone?: LucideIcon;
  tom?: string;
}

/**
 * Número de destaque do relatório.
 *
 * Difere do cartão do painel por receber o valor já em texto: um relatório
 * mistura contagem, percentual e duração, e formatá-los aqui dentro exigiria
 * que o cartão soubesse de qual bloco veio o número.
 */
export function Indicador({
  rotulo,
  valor,
  descricao,
  icone: Icone,
  tom = 'bg-silenciado text-silenciado-texto',
}: IndicadorProps) {
  return (
    <div className="bg-cartao cartao-relatorio flex items-start gap-3 rounded-xl border p-4">
      {Icone && (
        <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-lg', tom)}>
          <Icone className="size-4.5" aria-hidden="true" />
        </span>
      )}
      <span className="min-w-0">
        <span className="block text-2xl leading-tight font-semibold tabular-nums">{valor}</span>
        <span className="text-silenciado-texto block text-sm">{rotulo}</span>
        {descricao && <span className="text-silenciado-texto block text-xs">{descricao}</span>}
      </span>
    </div>
  );
}

interface CartaoGraficoProps {
  titulo: string;
  descricao?: string;
  children: React.ReactNode;
  className?: string;
}

/** Cartão de um gráfico ou tabela, marcado para não ser partido na impressão. */
export function CartaoGrafico({ titulo, descricao, children, className }: CartaoGraficoProps) {
  return (
    <Cartao className={cn('cartao-relatorio', className)}>
      <CartaoCabecalho>
        <CartaoTitulo>{titulo}</CartaoTitulo>
        {descricao && <CartaoDescricao>{descricao}</CartaoDescricao>}
      </CartaoCabecalho>
      <CartaoConteudo>{children}</CartaoConteudo>
    </Cartao>
  );
}
